import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * The currently-selected "scope" for standings / predictions queries.
 *   - `'global'` = every active user (admin-only; capped at top 40 server-side).
 *   - `'self'`   = just the requesting user. Used when a non-admin without
 *                  any groups views the standings — they see only themselves.
 *   - otherwise  = a MongoDB ObjectId string naming a group the user belongs to.
 *
 * Persisted in localStorage so the scope survives reloads, and reconciled
 * against `useMyGroups()` + the current user's role — if the persisted id is
 * no longer valid (left a group, group deleted, lost admin), we migrate to
 * the next-best fallback (first group, else `'self'`) and rewrite storage.
 *
 * Legacy: pre-existing storage entries may hold `'all'` (the union-of-my-
 * groups sentinel from before this refactor). They're migrated to `'global'`
 * on read; the admin/non-admin reconcile then picks the right final value.
 */
export type ActiveGroupId = 'global' | 'self' | string;

const STORAGE_KEY = 'mondy_active_group';
const DEFAULT_ID: ActiveGroupId = 'global';

// Shape check for a MongoDB ObjectId string — 24 lowercase hex chars. We
// don't try to confirm the group still exists here (the standings dropdown
// reconciles against `useMyGroups()`); this is just "does the stored value
// even look like something we wrote?"
const OBJECT_ID_RE = /^[0-9a-f]{24}$/;

function isValidStoredId(raw: string | null): raw is ActiveGroupId {
  if (raw === 'global') return true;
  if (raw === 'self') return true;
  if (raw && OBJECT_ID_RE.test(raw)) return true;
  return false;
}

interface ActiveGroupContextValue {
  activeGroupId: ActiveGroupId;
  setActiveGroupId: (id: ActiveGroupId) => void;
}

const ActiveGroupContext = createContext<ActiveGroupContextValue | null>(null);

function readFromStorage(): ActiveGroupId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    // Migrate legacy 'all' sentinel from before the global refactor.
    if (raw === 'all') {
      window.localStorage.setItem(STORAGE_KEY, DEFAULT_ID);
      return DEFAULT_ID;
    }
    if (isValidStoredId(raw)) return raw;
    if (raw) window.localStorage.removeItem(STORAGE_KEY);
    return DEFAULT_ID;
  } catch {
    return DEFAULT_ID;
  }
}

function writeToStorage(id: ActiveGroupId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* privacy mode — ignore */
  }
}

export function ActiveGroupProvider({ children }: { children: ReactNode }) {
  const [activeGroupId, setActiveGroupIdState] = useState<ActiveGroupId>(
    () => readFromStorage(),
  );

  const setActiveGroupId = useCallback((id: ActiveGroupId) => {
    setActiveGroupIdState(id);
    writeToStorage(id);
  }, []);

  // Keep tabs / windows roughly in sync — if someone changes the selection
  // elsewhere, reflect it here too.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue === 'all' ? DEFAULT_ID : e.newValue;
      setActiveGroupIdState(isValidStoredId(next) ? next : DEFAULT_ID);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ activeGroupId, setActiveGroupId }),
    [activeGroupId, setActiveGroupId],
  );

  return (
    <ActiveGroupContext.Provider value={value}>
      {children}
    </ActiveGroupContext.Provider>
  );
}

export function useActiveGroup(): ActiveGroupId {
  const ctx = useContext(ActiveGroupContext);
  if (!ctx) {
    throw new Error('useActiveGroup must be used within ActiveGroupProvider');
  }
  return ctx.activeGroupId;
}

export function useSetActiveGroup(): (id: ActiveGroupId) => void {
  const ctx = useContext(ActiveGroupContext);
  if (!ctx) {
    throw new Error('useSetActiveGroup must be used within ActiveGroupProvider');
  }
  return ctx.setActiveGroupId;
}

/**
 * Pick the right scope for a viewer who landed on an invalid one. Admins
 * default back to `'global'`; everyone else gets their oldest group, or
 * `'self'` if they aren't in any groups yet. Pure / synchronous so it can
 * be used both in reconcile effects and in 403-fallback handlers.
 */
export function fallbackScopeFor(
  isAdmin: boolean,
  groupIds: string[],
): ActiveGroupId {
  if (isAdmin) return 'global';
  if (groupIds.length > 0) return groupIds[0]!;
  return 'self';
}
